// src/data/coverage/pathADocumentBindings.ts
// ============================================================
// PATH A DOCUMENTS -> MEASURES THEY EVIDENCE
//
// The three canonical ACME PDFs in demo-assets/ are the documents already present in the
// opening state and the ones the presenter drops on camera.
//
// ─────────────────────────────────────────────────────────────────────────────
// ASSUMPTION — this binding does not exist anywhere else in the repo.
//
// Today these three PDFs are uploaded with NO measure binding at all. useDemoUploads
// records a filename and a size and nothing more; nothing reads a document and concludes
// anything about a measure. Without this table the demo opens with ZERO evidence-backed
// measures, which is the wrong opening picture: the whole point of the coverage model is
// that documents are the strongest tier, so a state with three strategic documents in it
// must show evidence somewhere.
//
// All three bind to the same seven Strategic Value measures because all three are board
// and strategy documents. That is deliberate, not an oversight: if a later pass reads
// them properly and finds they evidence different subsets, this file is where that lands.
// ─────────────────────────────────────────────────────────────────────────────
// ============================================================

export interface PathADocumentBinding {
  /** Real filename in demo-assets/. Matched case-insensitively against uploads. */
  fileName: string;
  /** Display label. Used verbatim inside the coverage source string. */
  label: string;
  measureIds: number[];
}

/** The seven Strategic Value measures every board and strategy document evidences. */
const STRATEGIC_VALUE_MEASURES = [5, 6, 7, 8, 21, 22, 24];

export const PATH_A_DOCUMENT_BINDINGS: readonly PathADocumentBinding[] = [
  {
    fileName: 'ACME_FY26_Board_Strategic_Plan.pdf',
    label: 'FY26 Board Strategic Plan',
    measureIds: [...STRATEGIC_VALUE_MEASURES],
  },
  {
    fileName: 'ACME_Annual_Report_2025.pdf',
    label: 'Annual Report 2025',
    measureIds: [...STRATEGIC_VALUE_MEASURES],
  },
  {
    fileName: 'ACME_Board_Priorities_Brief.pdf',
    label: 'Board Priorities Brief',
    measureIds: [...STRATEGIC_VALUE_MEASURES],
  },
] as const;

/** Match an uploaded filename to its binding. Case and path insensitive. */
export function bindingForFileName(fileName: string): PathADocumentBinding | undefined {
  const base = (fileName || '').split(/[\\/]/).pop()?.trim().toLowerCase() ?? '';
  if (!base) return undefined;
  return PATH_A_DOCUMENT_BINDINGS.find((b) => b.fileName.toLowerCase() === base);
}

/** Every measure any Path A document evidences. Deduplicated, ascending. */
export const ALL_PATH_A_MEASURE_IDS: readonly number[] = Array.from(
  new Set(PATH_A_DOCUMENT_BINDINGS.flatMap((b) => b.measureIds)),
).sort((a, b) => a - b);
